import './Inputs.scss';
import React, { useEffect, useId, useRef } from 'react';
import closeIcon from 'assets/icons/close-icon.svg';
import greenTick from 'assets/icons/correct.png';
import redError from 'assets/icons/error-icon.svg';
import { CommonFileInputProps } from 'constants/index';
import { ReactComponent as FileIcon } from 'assets/icons/file-icon.svg';

export function CommonFileInput({
  isEdit,
  file,
  errorMessage,
  labelPlaceholder,
  labelClasses,
  setFileVariable,
  formatMessage,
  acceptedFormats,
  disabled,
  setValidity,
  fileWasChanged,
  accept,
  placeholder
}: CommonFileInputProps) {
  const [imageValidity, setImageValidity] = React.useState<string>('noInput');
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  // The parent owns the file: when it drops it (close icon, refused upload), the
  // native input and the validity icon follow.
  useEffect(() => {
    if (!file) {
      setImageValidity('noInput');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [file]);

  useEffect(() => {
    if (file) {
      checkValidity(file.name.split('.').pop() || '');
      setValidity?.(imageValidity !== 'noInput' && imageValidity !== 'inputError');
    } else if (imageValidity !== 'noInput' && imageValidity !== 'inputError') {
      setValidity?.(true);
    } else {
      setValidity?.(false);
    }
  }, [imageValidity, file]);

  const handleFileVariable = (file: File | undefined) => {
    if (file) {
      const fileNameLowercase = file.name.toLowerCase();
      const updatedFile = new File([file], fileNameLowercase, { type: file.type });
      setFileVariable(updatedFile);
    }
  };

  const handleCloseClick = () => {
    setFileVariable(undefined);
  };

  const changeHandlerFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null && event.target.files[0]) {
      const fileExtension = event.target.files[0].name.split('.').pop();
      checkValidity(fileExtension || '');
      handleFileVariable(event.target.files[0]);
      if (fileWasChanged) {
        fileWasChanged(true);
      }
    }
  };

  const checkValidity = (fileExtension: string) => {
    // the parent compares extensions in lower case too (see hasAcceptedFormat)
    if (acceptedFormats?.includes(fileExtension.toLowerCase())) {
      setImageValidity('inputValid');
    } else {
      setImageValidity('inputError');
    }
  };

  return (
    <div className={isEdit ? 'file-input-container edit-file' : 'file-input-container'}>
      <p className={labelClasses}>{labelPlaceholder}</p>
      <div className="file-input-container__elements">
        {imageValidity === 'inputValid' && (
          <img className="file-input-container__icons" src={greenTick} alt="validation icon" />
        )}
        {imageValidity === 'inputError' && (
          <img className="file-input-container__icons" src={redError} alt="error icon" />
        )}
        <input
          type="file"
          id={inputId}
          ref={inputRef}
          accept={accept}
          disabled={disabled}
          data-placeholder={placeholder}
          className={imageValidity}
          onChange={(e) => {
            changeHandlerFile(e);
          }}
        />
        <FileIcon className="file-input-container__file-icon" />
        <div className="file-input-container__filename">
          {file && <p>{file.name}</p>}
          {file && <img src={closeIcon} alt="delete image" onClick={handleCloseClick} />}
        </div>
        {imageValidity === 'inputError' && <p className="file-input-container__error-message">{errorMessage}</p>}
      </div>
      <p className="file-input-container__format-message">{formatMessage}</p>
    </div>
  );
}
